"use client";

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import api from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";

type AuthContextType = {
    accessToken: string | null;
    setAccessToken: (token: string | null) => void;
    isAuthenticated: boolean;
    loading: boolean;
    logout: () => void;
};

const publicPaths = ['/', '/login', '/register', '/forgotpassword'];
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const hasChecked = useRef(false);
    const pathname = useLocation().pathname;
    const navigate = useNavigate();

    const isAuthenticated = !!accessToken;

    useEffect(() => {
        if (hasChecked.current) return;
        hasChecked.current = true;

        const tryRefresh = async () => {
            const gt = document.cookie
                .split("; ")
                .find(row => row.startsWith("gt="))
                ?.split("=")[1];

            if (!gt) {
                setLoading(false);
                if (!publicPaths.includes(pathname)) navigate('/');
                return;
            }

            try {
                const res = await api.get("/auth/check", {
                    headers: { Authorization: `Bearer ${gt}` },
                });

                if (res.status === 200) {
                    setAccessToken(gt);
                    if (publicPaths.includes(pathname)) navigate('/dashboard');
                }
            } catch {
                document.cookie = "gt=; path=/; max-age=0;";
                if (!publicPaths.includes(pathname)) navigate('/');
            } finally {
                setLoading(false);
            }
        };

        tryRefresh();
    }, []);

    useEffect(() => {
        if (loading) return;

        if (!isAuthenticated && !publicPaths.includes(pathname)) {
            navigate('/');
        }
    }, [isAuthenticated, loading]);

    const logout = () => {
        setAccessToken(null);
        document.cookie = "gt=; path=/; max-age=0;";
        navigate('/login');
    };

    return (
        <AuthContext.Provider value={{ accessToken, setAccessToken, isAuthenticated, loading, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};

export default AuthProvider;